import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { SendIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { apiAdminchangelogsPost } from '@r/api/generated/client.ts';
import type { ChangelogChangelogWriteModelTag } from '@r/api/generated/client.schemas.ts';
import { invalidateChangelogQueries } from '@r/components/changelogs/invalidate-changelog-queries.ts';
import { FileUploader } from '@r/components/import/file-uploader.tsx';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useUploadFile } from '@r/hooks/use-upload-file.ts';
import { Button } from '@ui/button.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { RichTextEditor } from '@ui/rich-text-editor.tsx';
import { ToggleGroup, ToggleGroupItem } from '@ui/toggle-group.tsx';
import { Card } from '@ui/card.tsx';

const TARGETS = ['admins', 'sales', 'users'] as const;
type Target = (typeof TARGETS)[number];

const TAG_OPTIONS: ChangelogChangelogWriteModelTag[] = ['feature', 'improvement', 'bugfix'];

const emptyColumn = { title: '', description: '', media: [] as File[] };

/**
 * Schéma : `tag` commun aux 3 colonnes ; une colonne « active » (titre OU description saisi)
 * doit avoir titre ET description ; au moins une colonne active.
 */
const getSchema = (t: ReturnType<typeof useTranslation>['t']) => {
  const column = z.object({
    title: z.string().trim(),
    description: z.string().trim(),
    media: z.array(z.instanceof(File)).max(1)
  });

  return z
    .object({
      tag: z.enum(['feature', 'improvement', 'bugfix'], {
        required_error: t('changelog.create.tag.required')
      }),
      columns: z.object({ admins: column, sales: column, users: column })
    })
    .superRefine((value, ctx) => {
      const active = TARGETS.filter(
        (target) => value.columns[target].title !== '' || value.columns[target].description !== ''
      );

      if (active.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['columns'],
          message: t('changelog.create.columns.required')
        });
        return;
      }

      for (const target of active) {
        if (value.columns[target].title === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['columns', target, 'title'],
            message: t('changelog.create.title.required')
          });
        }
        if (value.columns[target].description === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['columns', target, 'description'],
            message: t('changelog.create.description.required')
          });
        }
      }
    });
};

type FormValues = z.infer<ReturnType<typeof getSchema>>;

/**
 * Formulaire « Rédiger une nouveauté » : une catégorie commune, une colonne par cible.
 * « Publier » crée un changelog par colonne complète (N appels POST, succès partiel géré).
 */
export function ChangelogCreateForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const { onUpload, progresses, isUploading } = useUploadFile({ mediaType: 'assets' });
  const [publishedTargets, setPublishedTargets] = useState<Target[]>([]);
  const [isPublishing, setIsPublishing] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(getSchema(t)),
    mode: 'onChange',
    defaultValues: {
      tag: TAG_OPTIONS[0],
      columns: {
        admins: { ...emptyColumn },
        sales: { ...emptyColumn },
        users: { ...emptyColumn }
      }
    }
  });

  const onSubmit = async (values: FormValues) => {
    const targets = TARGETS.filter(
      (target) =>
        !publishedTargets.includes(target) &&
        values.columns[target].title !== '' &&
        values.columns[target].description !== ''
    );
    if (targets.length === 0) return;

    setIsPublishing(true);
    const results = await Promise.allSettled(
      targets.map(async (target) => {
        const column = values.columns[target];
        let media: string | undefined;

        if (column.media.length > 0) {
          const uploaded = await onUpload(column.media);
          const iri = uploaded?.[0]?.iri;
          if (!iri) throw new Error('media-upload-failed');
          media = iri;
        }

        await apiAdminchangelogsPost({
          title: column.title,
          tag: values.tag,
          description: column.description,
          target,
          ...(media ? { media } : {})
        });
      })
    );
    setIsPublishing(false);

    const succeeded = targets.filter((_, index) => results[index].status === 'fulfilled');
    const failed = results.filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );

    if (succeeded.length > 0) {
      await invalidateChangelogQueries(queryClient);
    }

    if (failed.length === 0) {
      toast.success(t('changelog.create.success', { number: succeeded.length }));
      navigate({ to: '/fiddy-admin/ressources/changelogs' });
      return;
    }

    setPublishedTargets((previous) => [...previous, ...succeeded]);
    showOrvalErrorToast(
      failed[0]?.reason,
      t('changelog.create.partial.error', { number: failed.length })
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <FormField
          control={form.control}
          name="tag"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t('changelog.create.category.label')}
              </FormLabel>
              <FormControl>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  value={field.value ?? ''}
                  onValueChange={(value) => {
                    if (value) field.onChange(value);
                  }}
                >
                  {TAG_OPTIONS.map((option) => (
                    <ToggleGroupItem
                      key={option}
                      value={option}
                      className="rounded-full px-5 data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                    >
                      {t(`changelog.tag.${option}`)}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Card className="grid gap-6 lg:grid-cols-3 p-6">
          {TARGETS.map((target) => {
            const published = publishedTargets.includes(target);

            return (
              <div key={target} className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(`changelog.create.column.${target}`)}
                  </h2>
                  {published && (
                    <span className="text-xs font-medium text-primary">
                      {t('changelog.create.column.published')}
                    </span>
                  )}
                </div>

                <FormField
                  control={form.control}
                  name={`columns.${target}.title`}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t('changelog.create.title.label')}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} disabled={published} error={Boolean(fieldState.error)} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`columns.${target}.description`}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t('changelog.create.description.label')}
                      </FormLabel>
                      <FormControl>
                        <RichTextEditor
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          disabled={published}
                          error={Boolean(fieldState.error)}
                          ariaLabel={t('changelog.create.description.label')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`columns.${target}.media`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FileUploader
                          value={field.value as File[]}
                          onValueChange={field.onChange}
                          maxFiles={1}
                          maxSize={2 * 1024 * 1024}
                          accept={{ 'image/*': [] }}
                          progresses={progresses}
                          disabled={published || isUploading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            );
          })}
        </Card>

        <div className="flex justify-end">
          <Button
            type="submit"
            size="lg"
            loading={isPublishing || isUploading}
            disabled={!form.formState.isValid}
          >
            <SendIcon className="size-4" />
            {t('changelog.create.submit')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
